import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../api/api';
import ItemQuantityField from '../../components/ItemQuantityField';
import { RestaurantAttendance } from '../../types/restaurantPersonnel';
import { extractApiErrorMessage } from '../../utils/apiError';
import { createIdempotencyKey as key } from '../../utils/operationKey';
import { formatCivilDate, formatCivilDateTime, formatHours } from '../../utils/restaurantPersonnelFormat';
import { PayrollHistoryItem } from '../../utils/restaurantEmployeePrint';

interface PayrollSnapshot {
  id: number;
  periodId: number;
  amount: number;
  workedHours: number;
  employeeName: string;
  status: string;
  evaluationCoefficient: number;
  calculationMethod?: string;
  hourlyRate?: number;
}
const snapshotLine = (snapshot: PayrollSnapshot) =>
  `період №${snapshot.periodId} · ${snapshot.status === 'closed' ? 'закрито' : 'затверджено'} · ${snapshot.employeeName} · ` +
  `${formatHours(snapshot.workedHours)} · коеф. ${snapshot.evaluationCoefficient.toFixed(4)}` +
  (snapshot.calculationMethod ? ` · ${snapshot.calculationMethod} · ставка ${snapshot.hourlyRate?.toFixed(2)} ₴` : '') +
  ` · ${snapshot.amount.toFixed(2)} ₴`;

interface Dossier {
  employmentId: number;
  year: number;
  month: number;
  fullName: string;
  position: string;
  characteristic: string;
  hasPhoto: boolean;
  evaluationCoefficient: number;
  payrollSource: string;
  hallsSnapshots: PayrollSnapshot[];
  salarySnapshots: PayrollSnapshot[];
  hallsAdjustments: Array<{ id: number; amount: number; reason: string }>;
  salaryAdjustments: Array<{ id: number; amount: number; reason: string }>;
  attendance: RestaurantAttendance[];
  medicalChecks: Array<{ id: number; examDate: string; nextDueDate: string | null; result: string; notes: string }>;
  issuedItems: Array<{ id: number; itemName: string; quantity: number; unit: string; issuedOn: string;
    expectedReturnOn: string | null; returnedOn: string | null; conditionNotes: string; notes: string }>;
}
const date = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const money = (amount: number) =>
  `${new Intl.NumberFormat('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)} ₴`;

const RestaurantDossierPanel: React.FC<{ employmentId: number; canEdit: boolean; canViewSalary?: boolean }> =
  ({ employmentId, canEdit, canViewSalary = false }) => {
  const [month, setMonth] = useState(date().slice(0, 7));
  const [record, setRecord] = useState<Dossier | null>(null);
  const [payrollHistory, setPayrollHistory] = useState<PayrollHistoryItem[] | null>(null);
  const [payrollHistoryError, setPayrollHistoryError] = useState('');
  const [payrollHistoryLoading, setPayrollHistoryLoading] = useState(false);
  const [characteristic, setCharacteristic] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const medicalKey = useRef(key());
  const itemKey = useRef(key());
  const returnKeys = useRef<Record<number, string>>({});
  const [medical, setMedical] = useState({ examDate: date(), nextDueDate: '', result: '', notes: '' });
  const [item, setItem] = useState({ itemName: '', quantity: '1', unit: 'шт.', issuedOn: date(), expectedReturnOn: '', conditionNotes: '', notes: '' });
  const [returnDates, setReturnDates] = useState<Record<number, string>>({});
  const prefix = `/restaurant-personnel/${employmentId}`;
  const load = useCallback(async (signal?: AbortSignal) => {
    const [year, selectedMonth] = month.split('-').map(Number);
    const response = await api.get<Dossier>(`${prefix}/dossier`, { params: { year, month: selectedMonth }, signal });
    if (signal?.aborted) return;
    setRecord(response.data);
    setCharacteristic(response.data.characteristic);
  }, [prefix, month]);
  useEffect(() => {
    const controller = new AbortController();
    setRecord(null);
    setError('');
    void load(controller.signal).catch(failure => {
      if (!controller.signal.aborted) setError(extractApiErrorMessage(failure, 'Не вдалося завантажити досьє ресторану.'));
    });
    return () => controller.abort();
  }, [load]);
  useEffect(() => {
    const controller = new AbortController();
    setPayrollHistory(null);
    setPayrollHistoryError('');
    if (!canViewSalary) {
      setPayrollHistoryLoading(false);
      return () => controller.abort();
    }
    setPayrollHistoryLoading(true);
    void api.get<PayrollHistoryItem[]>(
      `/restaurant-payroll/employments/${employmentId}/history`,
      { signal: controller.signal },
    ).then(response => {
      if (!controller.signal.aborted) setPayrollHistory(response.data);
    }).catch(failure => {
      if (!controller.signal.aborted)
        setPayrollHistoryError(extractApiErrorMessage(failure, 'Не вдалося завантажити історію зарплати ресторану.'));
    }).finally(() => {
      if (!controller.signal.aborted) setPayrollHistoryLoading(false);
    });
    return () => controller.abort();
  }, [employmentId, canViewSalary]);
  useEffect(() => {
    const controller = new AbortController();
    let url = '';
    setPhotoUrl('');
    if (record?.hasPhoto) void api.get<Blob>(`${prefix}/photo`, { responseType: 'blob', signal: controller.signal })
      .then(response => {
        if (controller.signal.aborted) return;
        url = URL.createObjectURL(response.data);
        setPhotoUrl(url);
      }).catch(failure => {
        if (!controller.signal.aborted) setError(extractApiErrorMessage(failure, 'Не вдалося завантажити захищене фото.'));
      });
    return () => { controller.abort(); if (url) URL.revokeObjectURL(url); };
  }, [prefix, record]);

  const perform = async (action: () => Promise<void>) => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError('');
    setMessage('');
    try { await action(); await load(); setMessage('Досьє оновлено.'); }
    catch (failure) {
      setError(extractApiErrorMessage(failure, 'Не вдалося підтвердити зміну. Оновіть досьє перед повтором.'));
    } finally { pending.current = false; setBusy(false); }
  };
  return <section aria-label="Досьє ресторану">
    <h3>Досьє ресторану</h3>
    <label>Місяць досьє<input type="month" min="2000-01" max="2200-12" value={month} disabled={busy}
      onChange={event => { if (event.target.value) setMonth(event.target.value); }} /></label>
    {error && <p role="alert">{error}</p>}
    {message && <p role="status">{message}</p>}
    {record && <>
      <h4>{record.fullName} · {record.position}</h4>
      {photoUrl && <img className="restaurant-personnel__photo" src={photoUrl} alt={`Фото: ${record.fullName}`} />}
      <label>Особиста характеристика<textarea maxLength={8000} value={characteristic} disabled={!canEdit || busy}
        onChange={event => setCharacteristic(event.target.value)} /></label>
      {canEdit && <div className="restaurant-personnel__actions">
        <button type="button" disabled={busy} onClick={() => void perform(async () => {
          await api.put(`${prefix}/dossier`, { characteristic });
        })}>Зберегти характеристику</button>
        <label>Захищене фото<input type="file" accept="image/jpeg,image/png,image/webp" disabled={busy}
          onChange={event => {
            const file = event.target.files?.[0];
            if (!file) return;
            void perform(async () => {
              const data = new FormData(); data.append('file', file);
              await api.put(`${prefix}/photo`, data);
            });
            event.target.value = '';
          }} /></label>
        {record.hasPhoto && <button type="button" disabled={busy} onClick={() => {
          if (window.confirm('Видалити фото лише з цього досьє ресторану?'))
            void perform(async () => { await api.delete(`${prefix}/photo`); });
        }}>Видалити фото</button>}
      </div>}
      <h4>Табель за місяць</h4>
      <p>{formatHours(record.attendance.reduce((sum, interval) => sum + interval.hoursWorked, 0))} ·
        {' '}коефіцієнт оцінювання {record.evaluationCoefficient.toFixed(4)}</p>
      {record.attendance.map(interval => <p key={interval.id}>{formatCivilDate(interval.workDate)}:
        {' '}{formatCivilDateTime(interval.checkInAt)} — {interval.checkOutAt ? formatCivilDateTime(interval.checkOutAt) : 'відкрито'}
        {' · '}{formatHours(interval.hoursWorked)} · {interval.comment}</p>)}
      <h4>Історичні знімки HALLS</h4>
      {record.payrollSource !== 'explicit-legacy-halls-snapshots'
        ? <p>Для цього працевлаштування немає явного мосту до архівних знімків HALLS. Глобальна зарплата чи виторг не підставляються.</p>
        : <>
          <details>
            <summary>Детальніше про історичний міст HALLS</summary>
            <p>Явний історичний міст HALLS. Знімки й коригування не перераховуються.</p>
          </details>
          {record.hallsSnapshots.map(snapshot => <p key={`halls-${snapshot.id}`}>HALLS · {snapshotLine(snapshot)}</p>)}
          {record.salarySnapshots.map(snapshot => <p key={`salary-${snapshot.id}`}>Зарплата · {snapshotLine(snapshot)}</p>)}
          {[...record.hallsAdjustments.map(adjustment => ({ ...adjustment, source: 'halls' })),
            ...record.salaryAdjustments.map(adjustment => ({ ...adjustment, source: 'salary' }))]
            .map(adjustment => <p key={`${adjustment.source}-${adjustment.id}`}>Коригування: {adjustment.amount.toFixed(2)} ₴ · {adjustment.reason}</p>)}
          {!record.hallsSnapshots.length && !record.salarySnapshots.length && <p>Затверджених знімків за місяць немає.</p>}
        </>}
      <h4>Підсумки ресторанної зарплати</h4>
      {!canViewSalary
        ? <p>Історія зарплати недоступна без окремого дозволу на перегляд зарплати. Решта досьє завантажена окремо.</p>
        : payrollHistoryLoading
          ? <p role="status">Завантаження історії зарплати…</p>
          : payrollHistoryError
            ? <p role="alert">{payrollHistoryError}</p>
            : payrollHistory?.length
              ? payrollHistory.map(period => <article key={`${period.year}-${period.month}`}>
                <h5>{String(period.month).padStart(2, '0')}.{period.year} · {period.status === 'closed' ? 'Закрито' : 'Погоджено'}</h5>
                <p>{period.employeeName} · {period.positionSnapshot}</p>
                <p>Нараховано: {money(period.amount)} · база: {money(period.baseSalary)} · бонус: {money(period.bonus)} · штраф: {money(period.penalty)}</p>
                <p>Години: {formatHours(period.workedHours)} · коефіцієнт: {period.evaluationCoefficient.toFixed(4)}</p>
                {period.adjustments.map((adjustment, index) => <p key={`${period.year}-${period.month}-adjustment-${index}`}>
                  Коригування: {money(adjustment.amount)} · {adjustment.reason} · {adjustment.createdByUsername}
                </p>)}
              </article>)
              : payrollHistory !== null && <p>Затверджених або закритих ресторанних нарахувань ще немає.</p>}
      <h4>Медогляди</h4>
      {record.medicalChecks.map(check => <p key={check.id}>{formatCivilDate(check.examDate)} · {check.result} · наступний: {
        check.nextDueDate ? formatCivilDate(check.nextDueDate) : 'не задано'} · {check.notes}</p>)}
      {canEdit && <form onSubmit={event => {
        event.preventDefault();
        void perform(async () => {
          await api.post(`${prefix}/medical-checks`, { ...medical, nextDueDate: medical.nextDueDate || null },
            { headers: { 'Idempotency-Key': medicalKey.current } });
          medicalKey.current = key();
          setMedical({ examDate: date(), nextDueDate: '', result: '', notes: '' });
        });
      }}>
        <div className="restaurant-personnel__form">
          <label>Дата медогляду<input type="date" required disabled={busy} value={medical.examDate}
            onChange={event => setMedical({ ...medical, examDate: event.target.value })} /></label>
          <label>Наступний медогляд<input type="date" disabled={busy} value={medical.nextDueDate}
            onChange={event => setMedical({ ...medical, nextDueDate: event.target.value })} /></label>
          <label>Результат медогляду<input required maxLength={200} disabled={busy} value={medical.result}
            onChange={event => setMedical({ ...medical, result: event.target.value })} /></label>
          <label>Примітки медогляду<textarea maxLength={2000} disabled={busy} value={medical.notes}
            onChange={event => setMedical({ ...medical, notes: event.target.value })} /></label>
        </div>
        <button disabled={busy}>Додати медогляд</button>
      </form>}
      <h4>Видані речі (кадровий журнал, без складських операцій)</h4>
      {record.issuedItems.map(issued => <article key={issued.id}>
        <p>{issued.itemName} · {issued.quantity} {issued.unit} · видано {formatCivilDate(issued.issuedOn)}
          {' · '}повернуто: {issued.returnedOn ? formatCivilDate(issued.returnedOn) : 'ні'} · {issued.conditionNotes} · {issued.notes}</p>
        {canEdit && !issued.returnedOn && <div className="restaurant-personnel__actions">
          <label>Дата повернення: {issued.itemName}<input type="date" required value={returnDates[issued.id] ?? date()}
            disabled={busy} onChange={event => setReturnDates({ ...returnDates, [issued.id]: event.target.value })} /></label>
          <button type="button" disabled={busy || returnDates[issued.id] === ''} onClick={() => void perform(async () => {
            const operationKey = returnKeys.current[issued.id] ?? key();
            returnKeys.current[issued.id] = operationKey;
            await api.post(`${prefix}/issued-items/${issued.id}/return`,
              { returnedOn: returnDates[issued.id] ?? date(), conditionNotes: null },
              { headers: { 'Idempotency-Key': operationKey } });
            delete returnKeys.current[issued.id];
          })} title={`ID виданої речі: ${issued.id}`}>Повернути річ</button>
        </div>}
      </article>)}
      {canEdit && <form onSubmit={event => {
        event.preventDefault();
        void perform(async () => {
          await api.post(`${prefix}/issued-items`, { ...item, quantity: Number(item.quantity), expectedReturnOn: item.expectedReturnOn || null },
            { headers: { 'Idempotency-Key': itemKey.current } });
          itemKey.current = key();
          setItem({ itemName: '', quantity: '1', unit: 'шт.', issuedOn: date(), expectedReturnOn: '', conditionNotes: '', notes: '' });
        });
      }}>
        <ItemQuantityField items={[]} value={item.itemName} onChange={name => setItem({ ...item, itemName: name })}
          label="Назва виданої речі" quantity={item.quantity} onQuantityChange={quantity => setItem({ ...item, quantity })}
          unit={item.unit} disabled={busy} min={0.001} step={0.001}
          itemInput={<input aria-label="Назва виданої речі" required maxLength={200} disabled={busy} value={item.itemName}
            onChange={event => setItem({ ...item, itemName: event.target.value })} />} />
        <div className="restaurant-personnel__form">
          <label>Одиниця речі<input required maxLength={40} disabled={busy} value={item.unit} onChange={event => setItem({ ...item, unit: event.target.value })} /></label>
          <label>Дата видачі<input required type="date" disabled={busy} value={item.issuedOn} onChange={event => setItem({ ...item, issuedOn: event.target.value })} /></label>
          <label>Повернути до<input type="date" disabled={busy} value={item.expectedReturnOn} onChange={event => setItem({ ...item, expectedReturnOn: event.target.value })} /></label>
          <label>Стан речі<input maxLength={1000} disabled={busy} value={item.conditionNotes} onChange={event => setItem({ ...item, conditionNotes: event.target.value })} /></label>
          <label>Примітки видачі<textarea maxLength={2000} disabled={busy} value={item.notes} onChange={event => setItem({ ...item, notes: event.target.value })} /></label>
        </div>
        <button disabled={busy}>Додати видану річ</button>
      </form>}
      <div className="restaurant-personnel__actions">
        <button type="button" disabled={busy} onClick={() => void perform(async () => { await load(); })}>Оновити досьє</button>
      </div>
    </>}
  </section>;
};
export default RestaurantDossierPanel;
